<template>
  <div class="datepicker">
    <label v-if="label" :for="id">{{ label }}</label>
    <VueDatePicker
      :id="id"
      :name="name"
      v-model="pickerValue"
      :range="range"
      :enable-time-picker="false"
      auto-apply
      :formats="formats"
      :locale="nl"
      placeholder="Selecteer een periode"
      @update:model-value="onChange"
      @closed="onClose"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { ref, watch } from 'vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { nl } from 'date-fns/locale';

export default {
  name: 'DatePicker',
  components: { VueDatePicker },
  props: {
    name: String,
    id: String,
    label: String,
    range: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: [String, Array],
      default: null
    }
  },
  emits: ['selected', 'update:modelValue'],
  setup(props, { emit }) {
    const pickerValue = ref(null);
    const lastEmitted = ref(null);
    const formats = {
      input: (val) => {
        if (!val) return '';
        if (!Array.isArray(val)) {
          return moment(val).format('DD-MM-YYYY');
        }
        const [a, b] = val;
        if (!a || !b) {
          return moment(a || b).format('DD-MM-YYYY');
        }
        const [start, end] = sortDates(a, b);
        return `${formatDate(start)} - ${formatDate(end)}`;
      }
    };

    const formatDate = (d) => (d instanceof Date && !isNaN(d) ? moment(d).format('DD-MM-YYYY') : null);

    const sortDates = (a, b) => (a > b ? [b, a] : [a, b]);

    const emitValue = (val) => {
      if (!val) {
        emit('selected', props.id, null);
        emit('update:modelValue', null);
        return;
      }
      if (!props.range) {
        const formatted = formatDate(val);
        if (formatted === lastEmitted.value) return;
        lastEmitted.value = formatted;
        emit('selected', props.id, formatted);
        emit('update:modelValue', formatted);
        return;
      }
      const cleaned = val.map(formatDate);
      const serialized = cleaned.join('|');
      lastEmitted.value = serialized;
      emit('selected', props.id, cleaned);
      emit('update:modelValue', cleaned);
    };

    const parseDate = (val) => {
      if (!val) return null;
      const d = moment(val, 'DD-MM-YYYY', true);
      return d.isValid() ? d.toDate() : null;
    };

    const syncModelValue = (val) => {
      if (!props.range) {
        pickerValue.value = parseDate(val);
        return;
      }
      if (!Array.isArray(val) || !val.length) {
        pickerValue.value = null;
        return;
      }
      const [start, end] = val.map(parseDate);
      if (start && end) {
        pickerValue.value = sortDates(start, end);
        return;
      }
      if (start && !end) {
        pickerValue.value = [start, null];
        return;
      }
      pickerValue.value = null;
    };

    const onChange = (val) => {
      if (!props.range) {
        pickerValue.value = val;
        emitValue(val);
        return;
      }
      if (!Array.isArray(val)) return;
      const [a, b] = val;
      if (a && b) {
        const [start, end] = sortDates(a, b);
        pickerValue.value = [start, end];
        emitValue([start, end]);
        return;
      }
      if (a && !b) {
        pickerValue.value = [a, null];
      }
    };

    const onClose = () => {
      if (!pickerValue.value || !props.range) return;
      const [a, b] = pickerValue.value;
      if (a && !b) {
        emitValue([a, null]);
      }
    };

    watch(() => props.modelValue, syncModelValue, { immediate: true });

    return {
      pickerValue,
      onChange,
      onClose,
      nl: nl,
      formats
    };
  }
};
</script>

<style lang="scss" scoped>
.datepicker {
  .dp--theme-light {
    padding-top: 18px;
    --dp-border-radius: 3px;
    --dp-font-family: 'Nunito Sans', sans-serif;
    --dp-primary-color: $teal-darkest;
    --dp-border-color: #dddddd;
    --dp-icon-color: $teal-darkest;
  }
  .dp--input::placeholder {
    font-style: normal;
  }
  :deep(.dp--input-icon) {
    left: 8px;
    padding: 0px;
    top: 49%;
  }
  :deep(input) {
    height: 50px;
    padding-left: 30px;
    padding-right: 0px;
    cursor: pointer;
    border: 1px solid #ddd;
    width: 100%;
  }
  :deep(.dp--tp-wrap) {
    display: none;
  }
}
</style>
